import React from "react";

import SBCLinkCell from "ComparePlans/Components/SBCLinkCell";
import { useGetCurrentCompanyApplicationQuery, useGetUserExperienceFlagsQuery, useGetLifePlansQuery } from "EmployerApplication/api";
import { CoverageOptionsPageContext } from "EmployerApplication/pages/CoverageOptionsPage";
import { useSelector, useDispatch } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import formatCurrency from "Utils/formatCurrency";

import { LifeIcon } from "Cobalt/InsuranceTypeIcon";
import { Popover } from "Cobalt/Popover";
import { Table, TR, SnugTD } from "Cobalt/TableV2";
import { getCart, addPlanToCart } from "Common/CartService";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";
import LifeCostBreakdownModal from "Common/LifeCostBreakdownModal";
import { SizeSwitch } from "Common/Responsive";
import SIOverlayTrigger from "Common/SIOverlayTrigger";
import { Body2 } from "Theme/BodyText";
import Button from "Theme/Button";

import {
  ResponsiveAddCard,
  ResponsiveLoadingCard,
  ResponsiveSelectedCard,
  ResponsiveDisabledAddCard,
  SuccessStatus,
  FlexWrapper,
  PlanDetailsCollapse,
} from "./CompactAncillaryPlanCard";
import { useIsEligibleForLifePlans, LifePlanEligibilityMessage } from "./shoppingCartSection/LifePlanEligibility";
import LifePlanEligibilityWarning from "./shoppingCartSection/LifePlanEligibilityWarning";

const AddPlanCard = () => (
  <CoverageOptionsPageContext.Consumer>
    {({ showLifeModal }) => (
      <ResponsiveAddCard
        onClick={() => {
          AnalyticsTracking.trackEvent("cta_clicked", {
            text: "Add Life",
            location: "life_disability_new_entry",
          });

          showLifeModal();
        }}
        name="Life Insurance"
        additionalInfo={<SizeSwitch md={<Body2>Life Insurance costs $1-$3 per employee for a typical company</Body2>} />}
        icon={<LifeIcon />}
      />
    )}
  </CoverageOptionsPageContext.Consumer>
);

// TODO(disability): Use lifePlan information to generate correct additionalInfo content.
const AdditionalInfo = ({ lifePlan }) => (
  <>
    <Div mt={2} intuitProps={{ mt: 1 }}>
      <LifePlanEligibilityWarning plan={lifePlan} text="Plan Eligibility" />
    </Div>
    <SizeSwitch
      xs={
        <Body2 mt={2} intuitProps={{ mt: 1 }}>
          {formatCurrency(lifePlan.coverage_amount)} Plan
        </Body2>
      }
      md={<Body2>{formatCurrency(lifePlan.coverage_amount)}</Body2>}
    />
  </>
);

const ViewCostBreakdownButton = ({ lifePlan }) => (
  <LifeCostBreakdownModal
    type="life-cost-breakdown-modal"
    plan={lifePlan}
    triggerClassName="see-employee-costs"
    triggerText="View"
    trackOpen={() => {
      AnalyticsTracking.trackEvent("employee_costs_viewed", {
        location: "shopping_cart",
        plan_id: lifePlan.id,
        name: lifePlan.raw_name,
        brand: lifePlan.carrier,
        ages_entered: true,
      });
    }}
  />
);

const RemovePlanButton = () => {
  const dispatch = useDispatch();
  return (
    <Button
      fullWidth
      intuitProps={{ mx: "auto" }}
      color="destructive"
      onClick={() => {
        dispatch(addPlanToCart({ planType: "life", plan: null }));
        AnalyticsTracking.trackEvent("plan_removed", {
          type: "life",
          deprecatedName: `remove-plan/life`,
        });
      }}
    >
      Remove Plan
    </Button>
  );
};

const PlanCardContent = ({ lifePlan }) => (
  <>
    <FlexWrapper py={3} my={2} intuitProps={{ py: 2, my: 1 }}>
      <Body2 weight="bold">Total Monthly Cost</Body2>
      <Body2>{formatCurrency(lifePlan.total_cost, 2)}</Body2>
    </FlexWrapper>
    <PlanDetailsCollapse>
      <Div py={3} my={2} intuitProps={{ py: 2, my: 1 }}>
        <Table>
          <tbody>
            <TR>
              <SnugTD>Plan Document</SnugTD>
              <SnugTD>
                <SBCLinkCell plan={lifePlan} copy="Download" />
              </SnugTD>
            </TR>
            <TR>
              <SnugTD>Cost Breakdown</SnugTD>
              <SnugTD>
                <ViewCostBreakdownButton lifePlan={lifePlan} />
              </SnugTD>
            </TR>
          </tbody>
        </Table>
      </Div>
      <RemovePlanButton />
    </PlanDetailsCollapse>
  </>
);

const MobilePlanCardContent = ({ lifePlan }) => (
  <>
    <Div pt={5} pb={3} mb={2}>
      <Table>
        <tbody>
          <TR>
            <SnugTD>Plan Document</SnugTD>
            <SnugTD>
              <SBCLinkCell plan={lifePlan} copy="Download" />
            </SnugTD>
          </TR>
          <TR>
            <SnugTD>Cost Breakdown</SnugTD>
            <SnugTD>
              <ViewCostBreakdownButton lifePlan={lifePlan} />
            </SnugTD>
          </TR>
          <TR>
            <SnugTD>Total Monthly Cost</SnugTD>
            <SnugTD>{formatCurrency(lifePlan.total_cost, 2)}</SnugTD>
          </TR>
        </tbody>
      </Table>
    </Div>
    <RemovePlanButton />
  </>
);

/**
 * The SelectedCard component coordinates the differences of content when the selected
 * plan doesn't have a final quote or the employer needs to take some action.
 * @param {lifePlan} lifePlan
 * @returns
 */
// TODO(disability): Use lifePlan information to select the correct status and additionalInfo content.
const SelectedCard = ({ lifePlan }) => (
  <ResponsiveSelectedCard
    icon={<LifeIcon />}
    status={<SuccessStatus>Included</SuccessStatus>}
    name="Life Insurance"
    additionalInfo={<AdditionalInfo lifePlan={lifePlan} />}
  >
    <SizeSwitch xs={<MobilePlanCardContent lifePlan={lifePlan} />} md={<PlanCardContent lifePlan={lifePlan} />} />
  </ResponsiveSelectedCard>
);

const NoLongerEligibleCard = () => (
  <ResponsiveDisabledAddCard
    icon={<LifeIcon color="gray" />}
    name="Life Insurance"
    additionalInfo={
      <>
        <Body2 componentClass="span" color="warning">
          You are no longer eligible for life insurance{" "}
        </Body2>
        <SIOverlayTrigger
          placement="top"
          overlay={
            <Popover showBackdrop={false} id="more-reasons">
              {LifePlanEligibilityMessage}
            </Popover>
          }
        >
          <Icon icon="question-circle" />
        </SIOverlayTrigger>
      </>
    }
  />
);

/**
 * The LifeCompactCard component is responsible for gathering the necessary data to
 * render the correct version of the LifePlanCard component.
 * The versions are:
 * - Empty
 * - Loading
 * - Selected
 * - NoLongerEligible
 * @returns
 */
const LifeCompactCard = () => {
  const { data: { companyIDToken } = {} } = useGetCurrentCompanyApplicationQuery();
  const { life: lifePlanId } = useSelector(getCart);
  const { data: availablePlans } = useGetLifePlansQuery({ companyIDToken }, { skip: !lifePlanId });

  const lifePlan = availablePlans?.results?.find((plan) => plan.id === lifePlanId);

  const { data: userExperienceFlags } = useGetUserExperienceFlagsQuery({
    companyIDToken,
  });

  const hasAddedLifePlan = userExperienceFlags?.added_life_plan;
  const lifePlanEligible = useIsEligibleForLifePlans();

  return !lifePlanEligible && hasAddedLifePlan ? (
    <NoLongerEligibleCard />
  ) : lifePlan ? (
    <SelectedCard lifePlan={lifePlan} />
  ) : lifePlanId ? (
    <ResponsiveLoadingCard icon={<LifeIcon />} name="Life Insurance" />
  ) : (
    <AddPlanCard />
  );
};

export default LifeCompactCard;
